/**
 * Sage ERP Integration Dashboard
 * Tabbed interface for Sage connection configuration, order transmissions, and sync logs.
 */
import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
  SelectChangeEvent,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Sync as SyncIcon,
  History as HistoryIcon,
  CloudUpload as UploadIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Replay as RetryIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import TabPanel from '../Common/TabPanel';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SageConfig {
  id?: string;
  base_url: string;
  api_key: string;
  auto_transmit_on_approval: boolean;
  auto_sync_inventory: boolean;
  sync_frequency_minutes: number;
  connection_status?: 'success' | 'failed' | 'unknown';
  last_connected_at?: string;
}

interface SageTransmission {
  id: string;
  iris_order_id: string;
  sage_reference: string | null;
  product_name: string;
  store_name: string;
  quantity: number;
  status: string;
  transmitted_at: string;
  error_message?: string;
}

interface SageSyncLog {
  id: string;
  sync_type: string;
  direction: string;
  status: string;
  records_processed: number;
  duration_seconds: number;
  started_at: string;
  error_message?: string;
}

// ---------------------------------------------------------------------------
// Status chip colour mapping
// ---------------------------------------------------------------------------

const transmissionStatusColor = (status: string): 'default' | 'info' | 'primary' | 'warning' | 'secondary' | 'success' | 'error' => {
  const map: Record<string, 'default' | 'info' | 'primary' | 'warning' | 'secondary' | 'success' | 'error'> = {
    pending: 'default',
    transmitted: 'info',
    acknowledged: 'primary',
    in_preparation: 'warning',
    shipped: 'secondary',
    delivered: 'success',
    failed: 'error',
  };
  return map[status] ?? 'default';
};

const syncLogStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
  const map: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
    completed: 'success',
    failed: 'error',
    running: 'warning',
  };
  return map[status] ?? 'default';
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const SageIntegrationDashboard: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // Tab state
  const [activeTab, setActiveTab] = useState(0);

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false,
    message: '',
    severity: 'info',
  });

  // -----------------------------------------------------------------------
  // TAB 1 state: Configuration
  // -----------------------------------------------------------------------
  const [configForm, setConfigForm] = useState<SageConfig>({
    base_url: '',
    api_key: '',
    auto_transmit_on_approval: false,
    auto_sync_inventory: false,
    sync_frequency_minutes: 15,
    connection_status: 'unknown',
  });

  // Load config on mount
  const { isLoading: configLoading, error: configError } = useQuery({
    queryKey: ['sageConfig'],
    queryFn: async () => {
      const response = await api.getSageConfig();
      const data = response.data as SageConfig;
      setConfigForm({
        id: data.id,
        base_url: data.base_url ?? '',
        api_key: data.api_key ?? '',
        auto_transmit_on_approval: data.auto_transmit_on_approval ?? false,
        auto_sync_inventory: data.auto_sync_inventory ?? false,
        sync_frequency_minutes: data.sync_frequency_minutes ?? 15,
        connection_status: data.connection_status ?? 'unknown',
        last_connected_at: data.last_connected_at,
      });
      return data;
    },
  });

  // Save config mutation
  const saveConfigMutation = useMutation({
    mutationFn: (data: SageConfig) => api.saveSageConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sageConfig'] });
      setSnackbar({ open: true, message: t('sage.configSaved'), severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: err.response?.data?.detail || t('sage.configSaveError'), severity: 'error' });
    },
  });

  // Test connection mutation
  const testConnectionMutation = useMutation({
    mutationFn: () => api.testSageConnection(configForm.id || ''),
    onSuccess: (response: any) => {
      const result = response.data;
      setConfigForm((prev) => ({
        ...prev,
        connection_status: result.success ? 'success' : 'failed',
      }));
      setSnackbar({
        open: true,
        message: result.success ? t('sage.connectionSuccess') : t('sage.connectionFailed'),
        severity: result.success ? 'success' : 'error',
      });
      queryClient.invalidateQueries({ queryKey: ['sageConfig'] });
    },
    onError: (err: any) => {
      setConfigForm((prev) => ({ ...prev, connection_status: 'failed' }));
      setSnackbar({ open: true, message: err.response?.data?.detail || t('sage.connectionFailed'), severity: 'error' });
    },
  });

  // -----------------------------------------------------------------------
  // TAB 2 state: Transmissions
  // -----------------------------------------------------------------------
  const [transmissionFilters, setTransmissionFilters] = useState({
    status: '',
    date_from: '',
    date_to: '',
  });

  const {
    data: transmissionsData,
    isLoading: transmissionsLoading,
    error: transmissionsError,
  } = useQuery({
    queryKey: ['sageTransmissions', transmissionFilters],
    queryFn: async () => {
      const params: any = {};
      if (transmissionFilters.status) params.status = transmissionFilters.status;
      if (transmissionFilters.date_from) params.date_from = transmissionFilters.date_from;
      if (transmissionFilters.date_to) params.date_to = transmissionFilters.date_to;
      const response = await api.getSageTransmissions(params);
      return response.data as SageTransmission[];
    },
    refetchInterval: activeTab === 1 ? 30000 : false,
  });

  // Retry transmission mutation
  const retryTransmissionMutation = useMutation({
    mutationFn: (id: string) => api.retrySageTransmission(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sageTransmissions'] });
      setSnackbar({ open: true, message: t('sage.retrySuccess'), severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: err.response?.data?.detail || t('sage.retryFailed'), severity: 'error' });
    },
  });

  // Sync order statuses mutation
  const syncOrderStatusesMutation = useMutation({
    mutationFn: () => api.syncSageOrderStatuses(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sageTransmissions'] });
      setSnackbar({ open: true, message: t('sage.syncStatusesSuccess'), severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: err.response?.data?.detail || t('sage.syncStatusesFailed'), severity: 'error' });
    },
  });

  // -----------------------------------------------------------------------
  // TAB 3 state: Sync Logs
  // -----------------------------------------------------------------------
  const [syncLogFilters, setSyncLogFilters] = useState({ sync_type: '' });

  const {
    data: syncLogsData,
    isLoading: syncLogsLoading,
    error: syncLogsError,
  } = useQuery({
    queryKey: ['sageSyncLogs', syncLogFilters],
    queryFn: async () => {
      const params: any = {};
      if (syncLogFilters.sync_type) params.sync_type = syncLogFilters.sync_type;
      const response = await api.getSageSyncLogs(params);
      return response.data as SageSyncLog[];
    },
    refetchInterval: activeTab === 2 ? 60000 : false,
  });

  // Manual sync mutations
  const syncInventoryMutation = useMutation({
    mutationFn: () => api.syncSageInventory(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sageSyncLogs'] });
      setSnackbar({ open: true, message: t('sage.syncInventorySuccess'), severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: err.response?.data?.detail || t('sage.syncInventoryFailed'), severity: 'error' });
    },
  });

  const importSalesMutation = useMutation({
    mutationFn: () => api.importSageSales(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sageSyncLogs'] });
      setSnackbar({ open: true, message: t('sage.importSalesSuccess'), severity: 'success' });
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: err.response?.data?.detail || t('sage.importSalesFailed'), severity: 'error' });
    },
  });

  // CSV upload mutations
  const inventoryCsvRef = useRef<HTMLInputElement | null>(null);
  const salesCsvRef = useRef<HTMLInputElement | null>(null);

  const importInventoryCsvMutation = useMutation({
    mutationFn: (file: File) => api.importInventoryCsv(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sageSyncLogs'] });
      setSnackbar({ open: true, message: t('sage.importInventoryCsvSuccess'), severity: 'success' });
      if (inventoryCsvRef.current) inventoryCsvRef.current.value = '';
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: err.response?.data?.detail || t('sage.importInventoryCsvFailed'), severity: 'error' });
    },
  });

  const importSalesCsvMutation = useMutation({
    mutationFn: (file: File) => api.importSalesCsv(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sageSyncLogs'] });
      setSnackbar({ open: true, message: t('sage.importSalesCsvSuccess'), severity: 'success' });
      if (salesCsvRef.current) salesCsvRef.current.value = '';
    },
    onError: (err: any) => {
      setSnackbar({ open: true, message: err.response?.data?.detail || t('sage.importSalesCsvFailed'), severity: 'error' });
    },
  });

  const handleInventoryCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importInventoryCsvMutation.mutate(file);
    }
  };

  const handleSalesCsvUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      importSalesCsvMutation.mutate(file);
    }
  };

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleConfigTextChange = (field: keyof SageConfig) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setConfigForm((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const handleConfigSwitchChange = (field: keyof SageConfig) => (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setConfigForm((prev) => ({ ...prev, [field]: checked }));
  };

  const handleSyncFrequencyChange = (event: SelectChangeEvent<number>) => {
    setConfigForm((prev) => ({ ...prev, sync_frequency_minutes: Number(event.target.value) }));
  };

  const formatDateTime = (iso: string | null | undefined): string => {
    if (!iso) return '-';
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const formatDuration = (seconds: number | null | undefined): string => {
    if (seconds == null) return '-';
    if (seconds < 60) return `${seconds.toFixed(1)}s`;
    return `${Math.floor(seconds / 60)}m ${Math.round(seconds % 60)}s`;
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {t('sage.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('sage.subtitle')}
      </Typography>

      <Card sx={{ mt: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<SettingsIcon />} label={t('sage.configuration')} iconPosition="start" />
          <Tab icon={<SyncIcon />} label={t('sage.transmissions')} iconPosition="start" />
          <Tab icon={<HistoryIcon />} label={t('sage.syncLogs')} iconPosition="start" />
        </Tabs>

        {/* ============================================================= */}
        {/* TAB 1 - Connection & Configuration                            */}
        {/* ============================================================= */}
        <TabPanel value={activeTab} index={0}>
          {configLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {configError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(configError as any)?.response?.data?.detail || t('sage.configLoadError')}
            </Alert>
          )}

          {!configLoading && (
            <Box>
              {/* Connection Status */}
              <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="subtitle1">{t('sage.connectionStatus')}:</Typography>
                <Chip
                  icon={configForm.connection_status === 'success' ? <CheckCircleIcon /> : <ErrorIcon />}
                  label={configForm.connection_status === 'success' ? t('sage.connected') : configForm.connection_status === 'failed' ? t('sage.disconnected') : t('sage.unknown')}
                  color={configForm.connection_status === 'success' ? 'success' : configForm.connection_status === 'failed' ? 'error' : 'default'}
                  variant="outlined"
                />
                {configForm.last_connected_at && (
                  <Typography variant="body2" color="text.secondary">
                    {t('sage.lastConnected')}: {formatDateTime(configForm.last_connected_at)}
                  </Typography>
                )}
              </Box>

              {/* API Configuration Fields */}
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('sage.baseUrl')}
                    value={configForm.base_url}
                    onChange={handleConfigTextChange('base_url')}
                    placeholder="https://sage-api.example.com"
                    helperText={t('sage.baseUrlHelper')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label={t('sage.apiKey')}
                    type="password"
                    value={configForm.api_key}
                    onChange={handleConfigTextChange('api_key')}
                    helperText={t('sage.apiKeyHelper')}
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Toggle Switches */}
              <Typography variant="subtitle1" sx={{ mb: 2 }}>{t('sage.automationSettings')}</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={configForm.auto_transmit_on_approval}
                        onChange={handleConfigSwitchChange('auto_transmit_on_approval')}
                        color="primary"
                      />
                    }
                    label={t('sage.autoTransmitOnApproval')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={configForm.auto_sync_inventory}
                        onChange={handleConfigSwitchChange('auto_sync_inventory')}
                        color="primary"
                      />
                    }
                    label={t('sage.autoSyncInventory')}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('sage.syncFrequency')}</InputLabel>
                    <Select
                      value={configForm.sync_frequency_minutes}
                      label={t('sage.syncFrequency')}
                      onChange={handleSyncFrequencyChange}
                    >
                      <MenuItem value={5}>5 {t('sage.minutes')}</MenuItem>
                      <MenuItem value={10}>10 {t('sage.minutes')}</MenuItem>
                      <MenuItem value={15}>15 {t('sage.minutes')}</MenuItem>
                      <MenuItem value={30}>30 {t('sage.minutes')}</MenuItem>
                      <MenuItem value={60}>60 {t('sage.minutes')}</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Action Buttons */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => testConnectionMutation.mutate()}
                  disabled={testConnectionMutation.isPending || !configForm.base_url}
                  startIcon={testConnectionMutation.isPending ? <CircularProgress size={18} /> : <RefreshIcon />}
                >
                  {t('sage.testConnection')}
                </Button>
                <Button
                  variant="contained"
                  onClick={() => saveConfigMutation.mutate(configForm)}
                  disabled={saveConfigMutation.isPending}
                  startIcon={saveConfigMutation.isPending ? <CircularProgress size={18} /> : undefined}
                >
                  {t('sage.saveConfig')}
                </Button>
              </Box>
            </Box>
          )}
        </TabPanel>

        {/* ============================================================= */}
        {/* TAB 2 - Order Transmissions                                    */}
        {/* ============================================================= */}
        <TabPanel value={activeTab} index={1}>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>{t('sage.filterStatus')}</InputLabel>
              <Select
                value={transmissionFilters.status}
                label={t('sage.filterStatus')}
                onChange={(e: SelectChangeEvent) =>
                  setTransmissionFilters((prev) => ({ ...prev, status: e.target.value }))
                }
              >
                <MenuItem value="">{t('sage.all')}</MenuItem>
                <MenuItem value="pending">{t('sage.statusPending')}</MenuItem>
                <MenuItem value="transmitted">{t('sage.statusTransmitted')}</MenuItem>
                <MenuItem value="acknowledged">{t('sage.statusAcknowledged')}</MenuItem>
                <MenuItem value="in_preparation">{t('sage.statusInPreparation')}</MenuItem>
                <MenuItem value="shipped">{t('sage.statusShipped')}</MenuItem>
                <MenuItem value="delivered">{t('sage.statusDelivered')}</MenuItem>
                <MenuItem value="failed">{t('sage.statusFailed')}</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label={t('sage.dateFrom')}
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={transmissionFilters.date_from}
              onChange={(e) =>
                setTransmissionFilters((prev) => ({ ...prev, date_from: e.target.value }))
              }
            />
            <TextField
              label={t('sage.dateTo')}
              type="date"
              size="small"
              InputLabelProps={{ shrink: true }}
              value={transmissionFilters.date_to}
              onChange={(e) =>
                setTransmissionFilters((prev) => ({ ...prev, date_to: e.target.value }))
              }
            />
            <Button
              variant="outlined"
              onClick={() => syncOrderStatusesMutation.mutate()}
              disabled={syncOrderStatusesMutation.isPending}
              startIcon={syncOrderStatusesMutation.isPending ? <CircularProgress size={18} /> : <SyncIcon />}
            >
              {t('sage.syncStatuses')}
            </Button>
          </Box>

          {/* Loading */}
          {transmissionsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error */}
          {transmissionsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(transmissionsError as any)?.response?.data?.detail || t('sage.transmissionsLoadError')}
            </Alert>
          )}

          {/* Table */}
          {!transmissionsLoading && !transmissionsError && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('sage.irisOrderId')}</TableCell>
                    <TableCell>{t('sage.sageReference')}</TableCell>
                    <TableCell>{t('sage.product')}</TableCell>
                    <TableCell>{t('sage.store')}</TableCell>
                    <TableCell align="right">{t('sage.quantity')}</TableCell>
                    <TableCell>{t('sage.status')}</TableCell>
                    <TableCell>{t('sage.transmittedAt')}</TableCell>
                    <TableCell align="center">{t('sage.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {transmissionsData && transmissionsData.length > 0 ? (
                    transmissionsData.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.iris_order_id}</TableCell>
                        <TableCell>{row.sage_reference || '-'}</TableCell>
                        <TableCell>{row.product_name}</TableCell>
                        <TableCell>{row.store_name}</TableCell>
                        <TableCell align="right">{row.quantity}</TableCell>
                        <TableCell>
                          <Chip
                            label={t(`sage.status${row.status.charAt(0).toUpperCase() + row.status.slice(1).replace(/_(\w)/g, (_, c: string) => c.toUpperCase())}`)}
                            color={transmissionStatusColor(row.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{formatDateTime(row.transmitted_at)}</TableCell>
                        <TableCell align="center">
                          {row.status === 'failed' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={retryTransmissionMutation.isPending ? <CircularProgress size={14} /> : <RetryIcon />}
                              disabled={retryTransmissionMutation.isPending}
                              onClick={() => retryTransmissionMutation.mutate(row.id)}
                            >
                              {t('sage.retry')}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {t('sage.noTransmissions')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        {/* ============================================================= */}
        {/* TAB 3 - Sync Logs                                              */}
        {/* ============================================================= */}
        <TabPanel value={activeTab} index={2}>
          {/* Filters & Actions */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl sx={{ minWidth: 180 }} size="small">
              <InputLabel>{t('sage.filterSyncType')}</InputLabel>
              <Select
                value={syncLogFilters.sync_type}
                label={t('sage.filterSyncType')}
                onChange={(e: SelectChangeEvent) =>
                  setSyncLogFilters((prev) => ({ ...prev, sync_type: e.target.value }))
                }
              >
                <MenuItem value="">{t('sage.all')}</MenuItem>
                <MenuItem value="inventory">{t('sage.syncTypeInventory')}</MenuItem>
                <MenuItem value="sales">{t('sage.syncTypeSales')}</MenuItem>
                <MenuItem value="orders">{t('sage.syncTypeOrders')}</MenuItem>
                <MenuItem value="products">{t('sage.syncTypeProducts')}</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => syncInventoryMutation.mutate()}
              disabled={syncInventoryMutation.isPending}
              startIcon={syncInventoryMutation.isPending ? <CircularProgress size={18} /> : <SyncIcon />}
            >
              {t('sage.syncInventory')}
            </Button>
            <Button
              variant="outlined"
              onClick={() => importSalesMutation.mutate()}
              disabled={importSalesMutation.isPending}
              startIcon={importSalesMutation.isPending ? <CircularProgress size={18} /> : <SyncIcon />}
            >
              {t('sage.importSales')}
            </Button>
          </Box>

          {/* Loading */}
          {syncLogsLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Error */}
          {syncLogsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {(syncLogsError as any)?.response?.data?.detail || t('sage.syncLogsLoadError')}
            </Alert>
          )}

          {/* Table */}
          {!syncLogsLoading && !syncLogsError && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('sage.syncType')}</TableCell>
                    <TableCell>{t('sage.direction')}</TableCell>
                    <TableCell>{t('sage.status')}</TableCell>
                    <TableCell align="right">{t('sage.recordsProcessed')}</TableCell>
                    <TableCell align="right">{t('sage.duration')}</TableCell>
                    <TableCell>{t('sage.startedAt')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {syncLogsData && syncLogsData.length > 0 ? (
                    syncLogsData.map((log) => (
                      <TableRow key={log.id} hover>
                        <TableCell>
                          <Chip label={log.sync_type} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>{log.direction}</TableCell>
                        <TableCell>
                          <Chip
                            label={log.status}
                            color={syncLogStatusColor(log.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">{log.records_processed}</TableCell>
                        <TableCell align="right">{formatDuration(log.duration_seconds)}</TableCell>
                        <TableCell>{formatDateTime(log.started_at)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">
                        <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                          {t('sage.noSyncLogs')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {/* CSV Upload Section */}
          <Divider sx={{ my: 3 }} />
          <Typography variant="subtitle1" sx={{ mb: 2 }}>
            {t('sage.csvImport')}
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('sage.importInventoryCsv')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('sage.importInventoryCsvDesc')}
                  </Typography>
                  <input
                    ref={inventoryCsvRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={handleInventoryCsvUpload}
                  />
                  <Button
                    variant="outlined"
                    startIcon={importInventoryCsvMutation.isPending ? <CircularProgress size={18} /> : <UploadIcon />}
                    disabled={importInventoryCsvMutation.isPending}
                    onClick={() => inventoryCsvRef.current?.click()}
                  >
                    {t('sage.importInventoryCsv')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('sage.importSalesCsv')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('sage.importSalesCsvDesc')}
                  </Typography>
                  <input
                    ref={salesCsvRef}
                    type="file"
                    accept=".csv"
                    style={{ display: 'none' }}
                    onChange={handleSalesCsvUpload}
                  />
                  <Button
                    variant="outlined"
                    startIcon={importSalesCsvMutation.isPending ? <CircularProgress size={18} /> : <UploadIcon />}
                    disabled={importSalesCsvMutation.isPending}
                    onClick={() => salesCsvRef.current?.click()}
                  >
                    {t('sage.importSalesCsv')}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Card>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SageIntegrationDashboard;
